import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
// Api
import { GET_STREAM_MATCHUPS } from 'api/matchup/queries';
import { GET_STREAMS, GET_STREAM_HIGHLIGHTS } from 'api/streams/queries';
// Types
import {
  GetStreamMatchups,
  GetStreamMatchupsVariables,
} from 'api/matchup/types/GetStreamMatchups';
import {
  GetStream_stream_interview_interviewer,
  GetStream_stream_interview_interviewee,
} from 'api/streams/types/GetStream';
import { GetStreams, GetStreamsVariables } from 'api/streams/types/GetStreams';
import {
  GetStreamHighlights,
  GetStreamHighlightsVariables,
} from 'api/streams/types/GetStreamHighlights';
import { AccountType } from 'api/graphql-global-types';
// Helpers
import { mapInterviewStreamsToEventMedia } from 'helpers/streamEventMedia';
import { mapHighlightsToClips } from 'helpers/streamHighlights';
import { getEnvLink } from 'helpers/routes';
// Hooks
import { useAthleteRelatedStreams } from 'hooks';
// Ui
import Modal from 'ui3/Modal/Modal';
import VideoPlayer from 'ui/VideoPlayer/VideoPlayer';
// Components
import SocialShareQRButton from 'ui3/SocialShareQRButton/SocialShareQRButton';
// Components
import { StreamContentTabKey } from '../StreamContentTabs/StreamContentTabs';
import StreamContentTabPanels from '../StreamContentTabPanels/StreamContentTabPanels';
import StreamMatchups from '../StreamMatchups/StreamMatchups';
import StreamHighlights from '../StreamHighlights/StreamHighlights';
import StreamEventMedia from '../StreamEventMedia/StreamEventMedia';
import StreamParticipants from '../StreamParticipants/StreamParticipants';
import StreamUpcomingEvents from '../StreamUpcomingEvents/StreamUpcomingEvents';
// Types
import { HighlightClip } from '../StreamHighlights/types';
// Styles
import styles from './StreamContentSections.module.scss';

export const TAB_QUERY_PARAM = 'tab';
export const CLIP_QUERY_PARAM = 'clip';

const MEDIA_SHELF_LIMIT = 8;

const isTabKey = (value: unknown): value is StreamContentTabKey =>
  Object.values(StreamContentTabKey).includes(value as StreamContentTabKey);

type StreamContentSectionsProps = {
  streamId: string;
  streamName: string;
  streamUrl: string;
  streamStartDate: string;
  fanPollingEnabled: boolean;
  sharePickGraphicsEnabled: boolean;
  /** Gates the replay/clip/media shelves for events that opted into the content hub. */
  postEventContentHubEnabled: boolean;
  isVotingOpen: boolean;
  streamSlug: string;
  storeId?: string;
  storeSlug?: string;
  isStreamOver?: boolean;
  onPlayHighlight?: (clip: HighlightClip) => void;
  playingHighlightId?: string | null;
  hasParticipants: boolean;
  isInterview: boolean;
  hasPrice: boolean;
  interviewGuests: {
    interviewer: GetStream_stream_interview_interviewer | null;
    interviewee: GetStream_stream_interview_interviewee | null;
  } | null;
};

const StreamContentSections = ({
  streamId,
  streamName,
  streamUrl,
  streamStartDate,
  fanPollingEnabled,
  sharePickGraphicsEnabled,
  postEventContentHubEnabled,
  isVotingOpen,
  streamSlug,
  storeId,
  storeSlug,
  isStreamOver = false,
  onPlayHighlight,
  playingHighlightId = null,
  hasParticipants,
  isInterview,
  hasPrice,
  interviewGuests,
}: StreamContentSectionsProps): JSX.Element | null => {
  const router = useRouter();
  const [chosenTab, setChosenTab] = useState<StreamContentTabKey | null>(null);
  const [lightboxClip, setLightboxClip] = useState<HighlightClip | null>(null);

  const { data } = useQuery<GetStreamMatchups, GetStreamMatchupsVariables>(
    GET_STREAM_MATCHUPS,
    {
      variables: { streamId },
      skip: !streamId || !hasPrice || isInterview,
      fetchPolicy: 'cache-and-network',
    }
  );

  const matchups = useMemo(() => data?.getStreamMatchups || [], [data]);

  const { data: mediaData } = useQuery<GetStreams, GetStreamsVariables>(
    GET_STREAMS,
    {
      variables: {
        input: {
          ppvStreamId: streamId,
          accountTypes: [AccountType.Creator, AccountType.Basic],
          limit: MEDIA_SHELF_LIMIT,
        },
      },
      skip: !streamId || !postEventContentHubEnabled,
      fetchPolicy: 'cache-and-network',
    }
  );

  const media = useMemo(
    () =>
      mapInterviewStreamsToEventMedia(mediaData?.streamsV2?.entities, streamId),
    [mediaData, streamId]
  );

  const { data: highlightsData } = useQuery<
    GetStreamHighlights,
    GetStreamHighlightsVariables
  >(GET_STREAM_HIGHLIGHTS, {
    variables: { input: { slug: streamSlug } },
    skip: !streamSlug || !postEventContentHubEnabled,
    fetchPolicy: 'cache-and-network',
  });

  const highlights = useMemo(
    () => mapHighlightsToClips(highlightsData?.stream?.ppvHighlights),
    [highlightsData]
  );

  const upcomingEventsEnabled = Boolean(
    isStreamOver && postEventContentHubEnabled && storeId
  );
  const { streams: upcomingEvents } = useAthleteRelatedStreams({
    storeId: upcomingEventsEnabled ? storeId : undefined,
    currentStreamId: streamId,
    totalStreamsToShow: 10,
    upcomingOnly: true,
  });

  const [shareClip, setShareClip] = useState<HighlightClip | null>(null);

  const handleShareHighlight = (clip: HighlightClip) => {
    setShareClip(clip);
  };

  const shareClipPath = shareClip
    ? `${streamUrl.replace(getEnvLink(), '')}?${TAB_QUERY_PARAM}=${
        StreamContentTabKey.Highlights
      }&${CLIP_QUERY_PARAM}=${shareClip.id}`
    : '';

  const sharedClipOpenedRef = useRef(false);

  useEffect(() => {
    if (sharedClipOpenedRef.current) {
      return;
    }

    const sharedClipId = router.query[CLIP_QUERY_PARAM];
    const sharedClip =
      typeof sharedClipId === 'string'
        ? highlights.find((clip) => clip.id === sharedClipId)
        : undefined;

    if (!sharedClip) {
      return;
    }

    sharedClipOpenedRef.current = true;

    if (onPlayHighlight) {
      onPlayHighlight(sharedClip);
    } else {
      setLightboxClip(sharedClip);
    }
  }, [router.query, highlights, onPlayHighlight]);

  const availableTabs = useMemo(() => {
    const tabs: StreamContentTabKey[] = [];

    if (highlights.length > 0) {
      tabs.push(StreamContentTabKey.Highlights);
    }
    if (matchups.length > 0) {
      tabs.push(StreamContentTabKey.Matchups);
    }
    if (media.length > 0) {
      tabs.push(StreamContentTabKey.Media);
    }
    if (hasParticipants) {
      tabs.push(StreamContentTabKey.Participants);
    }
    if (upcomingEventsEnabled && upcomingEvents.length > 0) {
      tabs.push(StreamContentTabKey.UpcomingEvents);
    }

    return tabs;
  }, [
    highlights.length,
    matchups.length,
    media.length,
    hasParticipants,
    upcomingEventsEnabled,
    upcomingEvents.length,
  ]);

  const activeTab = useMemo<StreamContentTabKey | null>(() => {
    if (availableTabs.length === 0) {
      return null;
    }

    if (chosenTab && availableTabs.includes(chosenTab)) {
      return chosenTab;
    }

    const fromUrl = router.query[TAB_QUERY_PARAM];

    if (isTabKey(fromUrl) && availableTabs.includes(fromUrl)) {
      return fromUrl;
    }

    return availableTabs[0];
  }, [availableTabs, chosenTab, router.query]);

  const handleTabChange = (key: StreamContentTabKey) => {
    setChosenTab(key);

    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, [TAB_QUERY_PARAM]: key },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  if (availableTabs.length === 0 || !activeTab) {
    return null;
  }

  return (
    <div className={styles.root}>
      <StreamContentTabPanels
        availableTabs={availableTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        hideWhenSingleTab
        renderPanel={(key) => (
          <>
            {key === StreamContentTabKey.Highlights && (
              <StreamHighlights
                clips={highlights}
                playingClipId={playingHighlightId ?? lightboxClip?.id ?? null}
                onPlay={(clip) =>
                  onPlayHighlight
                    ? onPlayHighlight(clip)
                    : setLightboxClip(clip)
                }
                onShare={handleShareHighlight}
              />
            )}

            {key === StreamContentTabKey.Matchups && (
              <StreamMatchups
                streamId={streamId}
                matchups={matchups}
                fanPollingEnabled={fanPollingEnabled}
                sharePickGraphicsEnabled={sharePickGraphicsEnabled}
                isVotingOpen={isVotingOpen}
                streamName={streamName}
                streamUrl={streamUrl}
                streamStartDate={streamStartDate}
              />
            )}

            {key === StreamContentTabKey.Media && (
              <StreamEventMedia media={media} />
            )}

            {key === StreamContentTabKey.Participants && (
              <StreamParticipants
                streamId={streamId}
                isInterview={isInterview}
                interviewGuests={interviewGuests}
              />
            )}

            {key === StreamContentTabKey.UpcomingEvents && (
              <StreamUpcomingEvents
                streams={upcomingEvents}
                seeAllLink={storeSlug ? `/${storeSlug}/streams` : undefined}
              />
            )}
          </>
        )}
      />

      {shareClip && (
        <SocialShareQRButton
          modalTitle="Share Clip"
          modalText="Share this clip with your friends"
          path={shareClipPath}
          twitterText={shareClip.title}
          open
          onClose={() => setShareClip(null)}
        />
      )}

      {lightboxClip && (
        <Modal
          open
          onClose={() => setLightboxClip(null)}
          title={lightboxClip.title}
          titleAlign="left"
          titleVariant="h5"
          size="extra-large"
          className={styles.lightboxModal}
        >
          {/* Same shared player the AI-clip review panel uses for these clips. */}
          <VideoPlayer
            key={lightboxClip.id}
            wrapperClassName={styles.lightboxPlayer}
            videoClassName={styles.lightboxVideo}
            src={lightboxClip.clipUrl}
            autoPlay
            volumeControl="speaker"
            videoPoster={lightboxClip.thumbnailUrl || undefined}
          />
        </Modal>
      )}
    </div>
  );
};

export default StreamContentSections;
